import { useState } from "react";
import { ArrowUp, Eye, EyeOff, Filter as FilterIcon, X } from "lucide-react";
import { Country, State, City } from "country-state-city";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Advertiser } from "./types";
import { downloadPrivateDocument, hasDocumentUrl, openPrivateDocument } from "../../../utils/function";
import { showToast } from "../../../components/toast";
import { Popover } from "../../../components/popover";
import SearchSelect from "../../../components/search-select";
import DocumentUpload from "../../../components/file-upload";
import Input from "../../../components/input";
import useApi from "../../../components/hooks/req";
import { businessCategories, businessType } from "../../auth/data";
import { CityIcon, CloudUpload, GlobeIcon, LocationIcon, LockIcon, NumberIcon } from "../../../assets/icon";

export const StatusBadge = ({ status }: { status: string }) => {
  // Normalize the status to match our config keys
  const normalizedStatus = status?.toLowerCase() === 'verified' ? 'Verified' 
    : status?.toLowerCase() === 'rejected' ? 'Rejected' 
    : 'Pending';
    
  const config = {
    Verified: {
      bg: 'bg-[#E6F9F1]',
      text: 'text-[#00875A]',
      icon: '✅',
      label: 'Verified',
    },
    Pending: {
      bg: 'bg-[#FFF3E0]',
      text: 'text-[#E65100]',
      icon: '🔄',
      label: 'Pending',
    },
    Rejected: {
      bg: 'bg-[#FDECEA]',
      text: 'text-[#C62828]',
      icon: '❗',
      label: 'Rejected',
    },
  };

  const statusConfig = config[normalizedStatus as keyof typeof config];

  if (!statusConfig) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        {status || 'Pending'}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
      {normalizedStatus === 'Verified' && (
        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="6" fill="#00875A" />
          <path d="M3.5 6l1.8 1.8 3.2-3.6" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {normalizedStatus === 'Pending' && (
        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="6" fill="#E65100" />
          <path d="M6 3.5v2.8l1.5 1.5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      )}
      {normalizedStatus === 'Rejected' && (
        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="6" fill="#C62828" />
          <path d="M4 4l4 4M8 4l-4 4" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      )}
      {statusConfig.label}
    </span>
  );
};

export const ThreeDotMenu = ({
  documentUrl,
  documentLabel,
  onUpdateStatus,
}: {
  documentUrl?: string | null;
  documentLabel: string;
  onUpdateStatus: () => void;
}) => {
  const documentAvailable = hasDocumentUrl(documentUrl);

  return (
    <Popover
      trigger={({ toggle }) => (
        <button
          onClick={toggle}
          className="text-gray-400 hover:text-gray-600 px-1 text-lg leading-none"
        >
          •••
        </button>
      )}
      menuClassName="w-48 bg-white border border-gray-100 rounded-xl shadow-lg py-1"
    >
      {(close) => (
        <>
          <button
            onClick={async () => {
              close();
              if (!documentAvailable) {
                showToast('No document has been uploaded yet', false);
                return;
              }
              try {
                await openPrivateDocument(documentUrl!);
              } catch (error) {
                showToast(error instanceof Error ? error.message : 'Failed to load document', false);
              }
            }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${!documentAvailable ? 'text-gray-400' : ''}`}
          >
            {documentLabel}
          </button>
          {documentAvailable && (
            <button
              onClick={async () => {
                close();
                try {
                  await downloadPrivateDocument(documentUrl!);
                } catch (error) {
                  showToast(error instanceof Error ? error.message : 'Failed to download document', false);
                }
              }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
            >
              Download Document
            </button>
          )}
          <button
            onClick={() => { close(); onUpdateStatus(); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
          >
            Update Verification Status
          </button>
        </>
      )}
    </Popover>
  );
};

// Combines the CAC + Director ID document actions into one menu, for the single merged
// "Verification" column (replacing the old separate Bus. CAC Status / Dir. ID Status columns).
export const VerificationMenu = ({
  cacDocumentUrl,
  directorDocumentUrl,
  onUpdateCacStatus,
  onUpdateDirectorStatus,
}: {
  cacDocumentUrl?: string | null;
  directorDocumentUrl?: string | null;
  onUpdateCacStatus: () => void;
  onUpdateDirectorStatus: () => void;
}) => {
  const cacAvailable = hasDocumentUrl(cacDocumentUrl);
  const directorAvailable = hasDocumentUrl(directorDocumentUrl);

  const viewDoc = async (url: string | null | undefined, label: string) => {
    if (!hasDocumentUrl(url)) {
      showToast(`No ${label} has been uploaded yet`, false);
      return;
    }
    try {
      await openPrivateDocument(url);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to load document', false);
    }
  };

  const downloadDoc = async (url: string | null | undefined, label: string) => {
    if (!hasDocumentUrl(url)) return;
    try {
      await downloadPrivateDocument(url);
    } catch (error) {
      showToast(error instanceof Error ? error.message : `Failed to download ${label}`, false);
    }
  };

  return (
    <Popover
      align="left"
      trigger={({ toggle }) => (
        <button
          onClick={toggle}
          className="text-gray-400 hover:text-gray-600 px-1 text-lg leading-none"
        >
          •••
        </button>
      )}
      menuClassName="w-56 bg-white border border-gray-100 rounded-xl shadow-lg py-1"
    >
      {(close) => (
        <>
          <button
            onClick={() => { close(); viewDoc(cacDocumentUrl, 'CAC document'); }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${!cacAvailable ? 'text-gray-400' : ''}`}
          >
            View Certificate (CAC)
          </button>
          {cacAvailable && (
            <button
              onClick={() => { close(); downloadDoc(cacDocumentUrl, 'CAC document'); }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
            >
              Download Certificate (CAC)
            </button>
          )}
          <button
            onClick={() => { close(); viewDoc(directorDocumentUrl, "Director's Government ID"); }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${!directorAvailable ? 'text-gray-400' : ''}`}
          >
            View Director's ID
          </button>
          {directorAvailable && (
            <button
              onClick={() => { close(); downloadDoc(directorDocumentUrl, "Director's Government ID"); }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
            >
              Download Director's ID
            </button>
          )}
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={() => { close(); onUpdateCacStatus(); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
          >
            Update CAC Status
          </button>
          <button
            onClick={() => { close(); onUpdateDirectorStatus(); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
          >
            Update Director ID Status
          </button>
        </>
      )}
    </Popover>
  );
};

// ─── Integrations Menu ────────────────────────────────────────────────────────

export const IntegrationsCell = ({ count }: { count: number }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => count > 0 && setShowModal(true)}
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${count > 0 ? 'bg-[#E6F9F1] text-[#00875A] cursor-pointer' : 'bg-[#FFF3E0] text-[#E65100] cursor-default'
          }`}
      >
        {count} Active
        {count > 0 && <span className="ml-1">•••</span>}
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-[90vw] max-w-md shadow-xl">
            <div className="flex justify-between items-start mb-1">
              <h3 className="text-lg font-bold">Active Integrations</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              There {count === 1 ? 'is' : 'are'} {count} active integration{count !== 1 ? 's' : ''} in this account
            </p>
            <div className="space-y-2">
              {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 text-sm">
                  <svg className="w-5 h-5 text-[#00875A] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <circle cx="10" cy="10" r="10" fill="#00875A" />
                    <path d="M6 10l3 3 5-5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  Integration #{i + 1}
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowModal(false)}
              className="mt-5 w-full bg-black text-white py-3 rounded-full font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export const PlatformToggle = ({
  advertiser,
  onChange,
}: {
  advertiser: Advertiser;
  onChange: (id: number, value: boolean) => void;
}) => {
  const [modal, setModal] = useState<'confirm-disable' | 'confirm-enable' | 'disabled' | 'enabled' | null>(null);

  const handleToggleClick = () => {
    setModal(advertiser.platform_access ? 'confirm-disable' : 'confirm-enable');
  };

  const handleConfirm = () => {
    if (modal === 'confirm-disable') {
      onChange(advertiser.advertiser_id, false);
      setModal('disabled');
    } else if (modal === 'confirm-enable') {
      onChange(advertiser.advertiser_id, true);
      setModal('enabled');
    }
  };

  const isActive = advertiser.platform_access;

  return (
    <>
      <button
        onClick={handleToggleClick}
        className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${isActive ? 'bg-[#34C759]' : 'bg-gray-300'
          }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${isActive ? 'translate-x-6' : 'translate-x-0'
            }`}
        />
      </button>

      {/* Confirm Disable */}
      {modal === 'confirm-disable' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-[90vw] max-w-sm shadow-xl text-center">
            <h3 className="text-lg font-bold mb-3">Are you sure you want to disable this advertiser?</h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <img 
                src={advertiser.image_url || `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`} 
                alt={advertiser.business_name} 
                className="w-8 h-8 rounded-full object-cover" 
                onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`; }} 
              />
              <span className="font-semibold text-[#003320]">{advertiser.business_name}</span>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              This advertiser will lose access to the VouchLinks platform and will no longer be able to manage campaigns, view reports, or create new offers until access is restored.
            </p>
            <button onClick={handleConfirm} className="w-full bg-black text-white py-3 rounded-full font-medium mb-2">Yes, Disable Advertiser</button>
            <button onClick={() => setModal(null)} className="w-full border border-gray-200 py-3 rounded-full font-medium">Cancel</button>
          </div>
        </div>
      )}

      {/* Confirm Enable */}
      {modal === 'confirm-enable' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-[90vw] max-w-sm shadow-xl text-center">
            <h3 className="text-lg font-bold mb-3">Enable Advertiser Account?</h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <img 
                src={advertiser.image_url || `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`} 
                alt={advertiser.business_name} 
                className="w-8 h-8 rounded-full object-cover" 
                onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`; }} 
              />
              <span className="font-semibold text-[#003320]">{advertiser.business_name}</span>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              This advertiser will regain access to the VouchLinks platform and will be able to sign in, manage campaigns, view reports, and access all approved advertiser features.
            </p>
            <button onClick={handleConfirm} className="w-full bg-black text-white py-3 rounded-full font-medium mb-2">Yes, Enable Advertiser</button>
            <button onClick={() => setModal(null)} className="w-full border border-gray-200 py-3 rounded-full font-medium">Cancel</button>
          </div>
        </div>
      )}

      {/* Disabled success */}
      {modal === 'disabled' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-[90vw] max-w-sm shadow-xl text-center">
            <h3 className="text-lg font-bold mb-3">Advertiser Disabled</h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <img 
                src={advertiser.image_url || `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`} 
                alt={advertiser.business_name} 
                className="w-8 h-8 rounded-full object-cover" 
                onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`; }} 
              />
              <span className="font-semibold text-[#003320]">{advertiser.business_name}</span>
            </div>
            <p className="text-sm text-gray-500 mb-1">Platform access has been revoked successfully.</p>
            <p className="text-sm text-gray-500 mb-5">You can re-enable this advertiser at any time from Advertiser Controls by changing their account status to Active.</p>
            <button onClick={() => setModal(null)} className="w-full bg-black text-white py-3 rounded-full font-medium">Close</button>
          </div>
        </div>
      )}

      {/* Enabled success */}
      {modal === 'enabled' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-[90vw] max-w-sm shadow-xl text-center">
            <h3 className="text-lg font-bold mb-3">Advertiser Enabled</h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <img 
                src={advertiser.image_url || `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`} 
                alt={advertiser.business_name} 
                className="w-8 h-8 rounded-full object-cover" 
                onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${advertiser.business_name}&background=000&color=fff`; }} 
              />
              <span className="font-semibold text-[#003320]">{advertiser.business_name}</span>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Platform access has been restored successfully. This will restore platform access and allow the advertiser to resume using their account.
            </p>
            <button onClick={() => setModal(null)} className="w-full bg-black text-white py-3 rounded-full font-medium">Close</button>
          </div>
        </div>
      )}
    </>
  );
};

export const UpdateStatusModal = ({
  field,
  currentStatus,
  onSave,
  onClose,
}: {
  field: 'business_cac_status' | 'director_id_status';
  currentStatus: string;
  onSave: (status: string) => Promise<boolean> | void;
  onClose: () => void;
}) => {
  const [selected, setSelected] = useState<string>(currentStatus);
  const [saving, setSaving] = useState(false);
  const options = ['pending', 'verified', 'rejected'];

  const handleSave = async () => {
    setSaving(true);
    const result = await onSave(selected);
    setSaving(false);
    if (result !== false) {
      onClose();
    }
  };

  const displayLabel = (status: string) => {
    if (status === 'pending') return 'Pending Verification';
    if (status === 'verified') return 'Verified';
    if (status === 'rejected') return 'Reject';
    return status;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl p-6 w-[90vw] max-w-sm shadow-xl">
        <div className="flex justify-between items-start mb-1">
          <div>
            <h3 className="text-lg font-bold">Update Verification Status</h3>
            <p className="text-sm text-gray-500">Assess the submitted information and set the appropriate verification status.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
        </div>
        <div className="space-y-2 mt-4">
          {options.map((opt) => (
            <label
              key={opt}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${selected === opt ? 'border-[#00875A] bg-gray-50' : 'border-gray-100 bg-gray-50'
                }`}
            >
              <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected === opt ? 'border-[#00875A]' : 'border-gray-300'
                }`}>
                {selected === opt && <span className="w-2.5 h-2.5 rounded-full bg-[#00875A]" />}
              </span>
              <input type="radio" className="sr-only" checked={selected === opt} onChange={() => setSelected(opt)} />
              <span className="font-medium text-sm">{displayLabel(opt)}</span>
            </label>
          ))}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="mt-5 w-full bg-black text-white py-3 rounded-full font-medium disabled:opacity-60"
        >
          {saving ? 'Saving...' : 'Save Status'}
        </button>
      </div>
    </div>
  );
};

export const UpdateVisibilityModal = ({
  isVisible,
  onSave,
  onClose,
}: {
  isVisible: boolean;
  onSave: (visibility: 'visible' | 'invisible') => Promise<boolean> | void;
  onClose: () => void;
}) => {
  const [selected, setSelected] = useState<'visible' | 'invisible'>(isVisible ? 'visible' : 'invisible');
  const [saving, setSaving] = useState(false);
  const options: { value: 'visible' | 'invisible'; label: string }[] = [
    { value: 'visible', label: 'Visible' },
    { value: 'invisible', label: 'Hidden' },
  ];

  const handleSave = async () => {
    setSaving(true);
    const result = await onSave(selected);
    setSaving(false);
    if (result !== false) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl p-6 w-[90vw] max-w-sm shadow-xl">
        <div className="flex justify-between items-start mb-1">
          <div>
            <h3 className="text-lg font-bold">Update Advertiser Visibility</h3>
            <p className="text-sm text-gray-500">Update platform visibility of this advertiser</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
        </div>
        <div className="space-y-2 mt-4">
          {options.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${selected === opt.value ? 'border-[#00875A] bg-gray-50' : 'border-gray-100 bg-gray-50'
                }`}
            >
              <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected === opt.value ? 'border-[#00875A]' : 'border-gray-300'
                }`}>
                {selected === opt.value && <span className="w-2.5 h-2.5 rounded-full bg-[#00875A]" />}
              </span>
              <input type="radio" className="sr-only" checked={selected === opt.value} onChange={() => setSelected(opt.value)} />
              <span className="font-medium text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="mt-5 w-full bg-black text-white py-3 rounded-full font-medium disabled:opacity-60"
        >
          {saving ? 'Saving...' : 'Save Status'}
        </button>
      </div>
    </div>
  );
};

// Backend-driven sortable column header — clicking toggles that field as the active sort key
// (desc first, then asc on a second click), sent to the server as `sort_by`/`sort_order` params
// rather than sorting the already-fetched page client-side.
export function SortableHeader({
  label,
  field,
  sortBy,
  sortOrder,
  onSort,
}: {
  label: string;
  field: string;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
}) {
  const active = sortBy === field;
  return (
    <button onClick={() => onSort(field)} className="inline-flex items-center gap-1 hover:text-emerald-600">
      {label}
      <ArrowUp
        size={13}
        className={`transition-transform ${active && sortOrder === 'asc' ? 'rotate-180' : ''} ${active ? 'text-emerald-600' : 'text-gray-400'}`}
      />
    </button>
  );
}

// Inline column-header filter: a small funnel icon + count badge sitting right next to the
// column label (e.g. "Bus. CAC Status ⚲ ②"), opening a dropdown of that column's filter
// options. Replaces the old standalone "Label ▾" FilterDropdown row above the table.
export function ColumnFilter<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const optionCount = options.filter((o) => o.value !== 'All').length;

  return (
    <Popover
      align="left"
      trigger={({ toggle }) => (
        <button
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          className="inline-flex items-center gap-1 align-middle"
          aria-label="Filter"
        >
          <FilterIcon size={13} className={value !== 'All' ? 'text-emerald-600' : 'text-gray-400'} />
          <span className="flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[9px] font-semibold">
            {optionCount}
          </span>
        </button>
      )}
      menuClassName="bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[160px] normal-case font-normal tracking-normal"
    >
      {(close) =>
        options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { onChange(opt.value); close(); }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${value === opt.value ? 'font-semibold text-emerald-600' : ''}`}
          >
            {opt.label}
          </button>
        ))
      }
    </Popover>
  );
}

// Confirmed payload for POST /super-admin/advertisers (a dedicated admin-create endpoint —
// not the same one the advertiser's own self-signup flow uses). Deliberately narrower than the
// public signup form: no geo fields, no documents. An admin can fill in website/address
// details, geo, and verification documents afterward from the advertiser's own detail page,
// which already supports editing and document upload.
export interface NewAdvertiserPayload {
  first_name: string;
  last_name: string;
  email: string;
  whatsapp_number: string;
  business_name: string;
  website_url: string;
  business_type: string;
  business_category: string;
  business_address: string;
  country: string;
  state: string;
  city: string;
  postal_code: string;
  password: string;
  // ASSUMPTION FLAGGED: not part of the confirmed 9-field curl example for this endpoint —
  // sent alongside it anyway per the original design, harmless if the backend ignores it.
  image_url: string;
  company_registration_certificate: string;
  directors_government_id: string;
}

const EMPTY_NEW_ADVERTISER: NewAdvertiserPayload = {
  first_name: '', last_name: '', email: '', whatsapp_number: '',
  business_name: '', website_url: '', business_type: '', business_category: '',
  business_address: '', country: '', state: '', city: '', postal_code: '',
  password: '', image_url: '', company_registration_certificate: '', directors_government_id: '',
};

const PASSWORD_PATTERN = /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const FIELD_LABELS: Partial<Record<keyof NewAdvertiserPayload, string>> = {
  first_name: 'Advertiser First Name', last_name: 'Advertiser Last Name',
  email: 'Business Email Address', whatsapp_number: 'WhatsApp Number',
  business_name: 'Registered Business Name', website_url: 'Website/ Store URL',
  business_address: 'Business Address', country: 'Country', state: 'State', city: 'City',
  business_type: 'Business Type', business_category: 'Business Category',
};

// Manual advertiser creation — a global admin adding an advertiser account directly, rather
// than the advertiser signing up themselves. Collects the same fields (business type/category
// as dropdowns, cascading country/state/city, verification documents, password) and submits
// to POST /super-admin/advertisers.
export const AddAdvertiserModal = ({
  onSave,
  onClose,
}: {
  onSave: (payload: NewAdvertiserPayload) => Promise<boolean>;
  onClose: () => void;
}) => {
  const [form, setForm] = useState<NewAdvertiserPayload>(EMPTY_NEW_ADVERTISER);
  const [showPassword, setShowPassword] = useState(false);
  const [profileImageName, setProfileImageName] = useState('');
  const [isProfileUploading, setIsProfileUploading] = useState(false);
  const [cacFile, setCacFile] = useState<File[]>([]);
  const [directorIdFile, setDirectorIdFile] = useState<File[]>([]);
  const [isCacUploading, setIsCacUploading] = useState(false);
  const [isDirectorIdUploading, setIsDirectorIdUploading] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [availableStates, setAvailableStates] = useState<{ value: string; label: string }[]>([]);
  const [availableCities, setAvailableCities] = useState<{ value: string; label: string }[]>([]);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [saving, setSaving] = useState(false);

  const authToken = localStorage.getItem("authToken");

  // Same unauthenticated upload endpoint the signup flow uses (pages/auth/kyc.tsx) — no admin
  // auth header needed since it's the same public document-upload route.
  const { makeRequest: uploadFile } = useApi('/auth/auth/upload/document', 'POST');

  // Confirmed (see reward-image upload): a profile image needs a permanent CDN URL, which the
  // generic document endpoint above can't provide — it only returns a bare storage key plus a
  // short-lived signed link. POST /upload/image returns the real CDN URL instead.
  const { makeRequest: uploadImage } = useApi('/upload/image', 'POST', {
    Authorization: `Bearer ${authToken}`,
  });

  const allCountries = Country.getAllCountries().map((c: any) => ({ value: c.isoCode, label: c.name }));

  const update = (key: keyof NewAdvertiserPayload, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleCountryChange = (isoCode: string) => {
    setSelectedCountryCode(isoCode);
    const country = Country.getCountryByCode(isoCode);
    update('country', country?.name || isoCode);
    update('state', '');
    update('city', '');
    setAvailableCities([]);
    const states = State.getStatesOfCountry(isoCode);
    setAvailableStates(states.map((s: any) => ({ value: s.isoCode, label: s.name })));
  };

  const handleStateChange = (stateCode: string) => {
    update('state', stateCode);
    update('city', '');
    if (selectedCountryCode) {
      const cities = City.getCitiesOfState(selectedCountryCode, stateCode);
      setAvailableCities(cities.map((c: any) => ({ value: c.name, label: c.name })));
    }
  };

  // The upload endpoint returns `document_key` (permanent S3 key — what the payload should
  // carry) and `preview_url` (a presigned URL that expires in ~10 minutes). Prefer the key.
  const uploadOneFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('file', file);
    const [response] = await uploadFile(formData);
    if (response?.success) return response?.document_key || response?.preview_url || null;
    showToast(response?.message || 'Upload failed. Please try again.', false);
    return null;
  };

  const handleProfileUpload = async (file: File) => {
    setProfileImageName(file.name);
    setIsProfileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const [response] = await uploadImage(formData);
      const isSuccess = response?.status || response?.success;
      // ASSUMPTION FLAGGED: no confirmed sample response yet for this endpoint — only the
      // constraint on the field it feeds (image_url) is confirmed elsewhere (see the reward
      // image upload fix). Guessing the returned CDN URL lives under one of these common keys.
      const cdnUrl = response?.url || response?.image_url || response?.data?.url || response?.data?.image_url || response?.cdn_url;

      if (isSuccess && cdnUrl) {
        update('image_url', cdnUrl);
      } else {
        showToast(response?.message || 'Profile image upload failed. Please try again.', false);
        setProfileImageName('');
      }
    } catch {
      showToast('Profile image upload failed. Please try again.', false);
      setProfileImageName('');
    } finally {
      setIsProfileUploading(false);
    }
  };

  const handleDocUpload = async (files: File[], type: 'cac' | 'director_id') => {
    const setFiles = type === 'cac' ? setCacFile : setDirectorIdFile;
    const field = type === 'cac' ? 'company_registration_certificate' : 'directors_government_id';

    const file = files[0];
    if (!file) {
      // DocumentUpload calls onChange([]) when its delete icon is clicked — clear both the
      // local file and the uploaded reference so the "uploaded" state actually goes away.
      setFiles([]);
      update(field, '');
      return;
    }

    const setUploading = type === 'cac' ? setIsCacUploading : setIsDirectorIdUploading;
    setFiles(files);
    setUploading(true);
    try {
      const ref = await uploadOneFile(file);
      if (ref) update(field, ref);
    } catch {
      showToast('Upload failed. Please try again.', false);
    } finally {
      setUploading(false);
    }
  };

  const isPasswordValid = PASSWORD_PATTERN.test(form.password);
  const isEmailValid = !form.email || EMAIL_PATTERN.test(form.email);
  const requiredFields: (keyof NewAdvertiserPayload)[] = [
    'first_name', 'last_name', 'email', 'whatsapp_number', 'business_name', 'website_url',
    'business_address', 'country', 'state', 'city', 'business_type', 'business_category',
  ];

  // Inline field errors only show once a save has been attempted, so the form opens clean.
  const errFor = (key: keyof NewAdvertiserPayload): string | undefined =>
    attemptedSubmit && !String(form[key]).trim() ? `${FIELD_LABELS[key]} is required` : undefined;

  // Names the *first* thing that's missing/invalid so the toast on save gives a single, specific
  // reason to fix even without scrolling through the form.
  const getValidationError = (): string | null => {
    for (const key of requiredFields) {
      if (!form[key].trim()) return `${FIELD_LABELS[key]} is required`;
    }
    if (!isEmailValid) return 'Enter a valid business email address';
    if (!form.password.trim()) return 'Please set a password';
    if (!isPasswordValid) return 'Password must be at least 8 characters, with a number and a special character';
    return null;
  };

  const handleSave = async () => {
    setAttemptedSubmit(true);
    const validationError = getValidationError();
    if (validationError) {
      showToast(validationError, false);
      return;
    }
    setSaving(true);
    const success = await onSave(form);
    setSaving(false);
    if (success) onClose();
  };

  const sectionClass = 'bg-[#F8FAFC] rounded-2xl p-5 mb-5';
  const sectionTitleClass = 'text-base font-bold text-slate-900';
  const sectionSubtitleClass = 'text-sm text-[#65799A] mb-4';

  return (
    <div className="fixed inset-0 bg-black/45 z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl p-7 shadow-2xl relative mx-auto my-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">New Advertiser</h2>
            <p className="text-sm text-[#65799A] mt-1">
              Create an advertiser account and add the details required to get them set up on VouchLinks
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Basic Info */}
        <div className={sectionClass}>
          <h3 className={sectionTitleClass}>Basic Info</h3>
          <p className={sectionSubtitleClass}>Enter the advertiser's primary contact and business information.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[14px] font-bold text-[#121212] block mb-2">Advertiser Profile Image</label>
              <label className={`flex items-center gap-3 border border-[#12121233] rounded h-[56px] px-4 bg-white ${saving ? 'opacity-50' : 'cursor-pointer hover:border-gray-400'}`}>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  disabled={saving}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleProfileUpload(file);
                    e.target.value = '';
                  }}
                />
                <CloudUpload />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#00A86B] underline truncate">
                    {isProfileUploading ? 'Uploading...' : profileImageName || 'Upload Profile Image'}
                  </p>
                  <p className="text-[11px] text-gray-400">PNG or JPG · Max 5 MB</p>
                </div>
              </label>
            </div>
            <Input
              label="Advertiser First Name"
              placeholder="John"
              value={form.first_name}
              onChange={(e) => update('first_name', e.target.value)}
              readOnly={saving}
              error={errFor('first_name')}
            />
            <Input
              label="Advertiser Last Name"
              placeholder="Doe"
              value={form.last_name}
              onChange={(e) => update('last_name', e.target.value)}
              readOnly={saving}
              error={errFor('last_name')}
            />
            <Input
              label="Business Email Address"
              type="email"
              placeholder="e.g. john@businessemail.com"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              readOnly={saving}
              error={form.email && !isEmailValid ? 'Enter a valid email address' : errFor('email')}
            />
            <Input
              label="Registered Business Name"
              placeholder="e.g. Coca Cola"
              value={form.business_name}
              onChange={(e) => update('business_name', e.target.value)}
              readOnly={saving}
              error={errFor('business_name')}
            />
            <Input
              label="Website/ Store URL"
              placeholder="https://"
              value={form.website_url}
              onChange={(e) => update('website_url', e.target.value)}
              readOnly={saving}
              error={errFor('website_url')}
            />
          </div>
        </div>

        {/* Business Details */}
        <div className={sectionClass}>
          <h3 className={sectionTitleClass}>Business Details</h3>
          <p className={sectionSubtitleClass}>Add the advertiser's contact number and registered business location</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[14px] font-bold text-[#121212] block mb-2">
                WhatsApp Number<span className="text-red-500"> *</span>
              </label>
              <PhoneInput
                country={(selectedCountryCode || 'ng').toLowerCase()}
                value={form.whatsapp_number}
                onChange={(phone) => update('whatsapp_number', phone.replace(/^\+/, ''))}
                inputProps={{ name: 'whatsapp_number', disabled: saving }}
                containerClass="!w-full"
                inputClass="!w-full !h-[56px] !pl-14 !rounded !border !border-[#12121233] !text-sm"
                buttonClass="!h-[56px] !border-r !border-[#12121233] !rounded-l"
              />
              {errFor('whatsapp_number') && (
                <span className="text-[#FF0101] text-[12px] mt-1 block">{errFor('whatsapp_number')}</span>
              )}
            </div>
            <Input
              label="Business Address"
              placeholder="Business address"
              icon={<LocationIcon />}
              value={form.business_address}
              onChange={(e) => update('business_address', e.target.value)}
              readOnly={saving}
              error={errFor('business_address')}
            />
            <SearchSelect
              label="Business Type"
              name="business_type"
              icon={<CityIcon />}
              value={form.business_type}
              onChange={(v) => update('business_type', v)}
              options={businessType}
              disabled={saving}
              error={attemptedSubmit && !form.business_type ? 'Business type is required' : undefined}
            />
            <SearchSelect
              label="Business Category"
              name="business_category"
              icon={<CityIcon />}
              value={form.business_category}
              onChange={(v) => update('business_category', v)}
              options={businessCategories}
              disabled={saving}
              error={attemptedSubmit && !form.business_category ? 'Business category is required' : undefined}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <SearchSelect
              label="Country"
              name="country"
              icon={<GlobeIcon />}
              value={selectedCountryCode}
              onChange={handleCountryChange}
              options={allCountries}
              disabled={saving}
              error={attemptedSubmit && !selectedCountryCode ? 'Country is required' : undefined}
            />
            <SearchSelect
              label="State"
              name="state"
              icon={<GlobeIcon />}
              value={form.state}
              onChange={handleStateChange}
              options={availableStates}
              disabled={saving || !selectedCountryCode}
              error={attemptedSubmit && !form.state ? 'State is required' : undefined}
            />
            <SearchSelect
              label="City"
              name="city"
              icon={<GlobeIcon />}
              value={form.city}
              onChange={(v) => update('city', v)}
              options={availableCities}
              disabled={saving || !form.state}
              error={attemptedSubmit && !form.city ? 'City is required' : undefined}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Input
              label="Postal Code (Optional)"
              placeholder="100001"
              icon={<NumberIcon />}
              value={form.postal_code}
              onChange={(e) => update('postal_code', e.target.value.replace(/\D/g, '').slice(0, 10))}
              readOnly={saving}
            />
            <Input
              type={showPassword ? 'text' : 'password'}
              label="Set a Password"
              placeholder="Set a password"
              icon={<LockIcon />}
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              readOnly={saving}
              rightIcon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              onRightIconClick={() => setShowPassword(!showPassword)}
              error={
                form.password && !isPasswordValid
                  ? 'At least 8 characters, with a number and a special character'
                  : attemptedSubmit && !form.password.trim()
                    ? 'Please set a password'
                    : undefined
              }
            />
          </div>
        </div>

        {/* Business and Identity Verification */}
        <div className={sectionClass}>
          <h3 className={sectionTitleClass}>Business and Identity Verification</h3>
          <p className={sectionSubtitleClass}>Optionally upload documents to verify the business and confirm the identity of its director — these can also be added later from the advertiser's profile</p>
          <div className="space-y-3">
            <div>
              <DocumentUpload
                label="Company Registration Certificate (CAC) (Optional)"
                description="Upload company registration certificate (JPEG or PDF, up to 10MB)."
                files={cacFile}
                onChange={(files) => handleDocUpload(files, 'cac')}
              />
              {isCacUploading && <p className="text-sm text-blue-600 mt-1">Uploading CAC document...</p>}
            </div>
            <div>
              <DocumentUpload
                label="Director's Government ID (Optional)"
                description="Valid govt-issued ID of company director (JPEG PDF, up to 10MB)"
                files={directorIdFile}
                onChange={(files) => handleDocUpload(files, 'director_id')}
              />
              {isDirectorIdUploading && <p className="text-sm text-blue-600 mt-1">Uploading Director ID...</p>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-6 pt-1">
          <button
            onClick={onClose}
            disabled={saving}
            className="text-sm font-semibold text-slate-900 hover:opacity-70 transition-opacity disabled:opacity-50"
          >
            Discard
          </button>
          <button
            onClick={handleSave}
            disabled={saving || isCacUploading || isDirectorIdUploading}
            className="px-7 py-3 rounded-full bg-[#111827] text-white font-semibold text-sm hover:bg-black transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save and activate'}
          </button>
        </div>
      </div>
    </div>
  );
};